import {
  Box,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RECIPIENT_EVENT_TYPE } from "../../../../common/constants/campaign";
import { CAMPAIGN_CONTEXT } from "../../../../common/types/campaign";
import { DataTable } from "../../../../components/data-table/DataTable";
import IButton, { BUTTON } from "../../../../components/IButton";
import ITitle from "../../../../components/ITitle";
import { useAppDispatch } from "../../../../store";
import { selectCampaign } from "../campaignSlice";
import {
  getRecipientAllData,
  getRecipientBounceData,
  getRecipientClickData,
  getRecipientNotSentData,
  getRecipientOpenData,
  getRecipientSpamData,
  getRecipientUnsubData,
  selectCampaignReports,
  setRecipientEventType,
  setRecipientsReportsPage,
} from "./campaignReportsSlice";
import {
  userColumnDef,
  bounceTableHeader,
  clickColumnDef,
  notSentTableHeader,
  openTableHeader,
  recipientTableHeader,
  spamTableHeader,
  unsubscribedTableHeader,
} from "./constantHelper";

function RecipientTable({
  type,
  showUserColumn,
}: {
  type: RECIPIENT_EVENT_TYPE;
  showUserColumn: boolean;
}) {
  const dispatch = useAppDispatch();
  const { recipientData } = useSelector(selectCampaignReports);
  const [viewLinks, setViewLinks] = useState<string[]>([]);

  function ViewLinkButton({ links }: { links: string[] }) {
    return (
      <IButton
        variant={BUTTON.SECONDARY}
        onClick={() => setViewLinks(links)}
        fontSize="12px"
        ml={3}
        height="30px"
        name="view-links"
      >
        View links
      </IButton>
    );
  }

  const columns: Record<string, ColumnDef<any>[]> = {
    [RECIPIENT_EVENT_TYPE.ALL]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...openTableHeader,
      ...clickColumnDef(ViewLinkButton),
    ],
    [RECIPIENT_EVENT_TYPE.NOT_SENT]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...notSentTableHeader,
    ],
    [RECIPIENT_EVENT_TYPE.OPEN]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...openTableHeader,
      ...clickColumnDef(ViewLinkButton),
    ],
    [RECIPIENT_EVENT_TYPE.CLICK]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...clickColumnDef(ViewLinkButton),
    ],
    [RECIPIENT_EVENT_TYPE.BOUNCE]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...bounceTableHeader,
    ],
    [RECIPIENT_EVENT_TYPE.UNSUB]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...unsubscribedTableHeader,
    ],
    [RECIPIENT_EVENT_TYPE.SPAM]: [
      ...userColumnDef(showUserColumn),
      ...recipientTableHeader,
      ...spamTableHeader,
    ],
  };

  function ViewLinksModal() {
    return (
      <Modal
        isOpen={!!viewLinks.length}
        onClose={() => setViewLinks([])}
        isCentered={true}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="18px">Link preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={3}>
            <VStack maxHeight="70vh" overflow="auto">
              {viewLinks.map((link, index) => {
                return (
                  <Box
                    w="100%"
                    key={index}
                    bg="gray.200"
                    p={1}
                    borderRadius="2px"
                  >
                    {link}
                  </Box>
                );
              })}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
      <DataTable
        loading={recipientData[type].fetchingList}
        list={recipientData[type].list}
        setPage={(pageNo) => dispatch(setRecipientsReportsPage(pageNo))}
        totalPageCount={recipientData[type].totalPageCount}
        currentPage={recipientData[type].currentPageNo}
        columns={columns[type]}
        emptyMsg="No data found"
      />
      <ViewLinksModal />
    </>
  );
}

export default function RecipientsReports() {
  const dispatch = useAppDispatch();

  const {
    campaignDetails: {
      data: { campaign_context },
    },
  } = useSelector(selectCampaign);

  const {
    recipientData,
    recipientEventType,
    filterData: { timeRange, templateId },
  } = useSelector(selectCampaignReports);

  const recipientAllDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.ALL].currentPageNo;
  const recipientNotsentDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.NOT_SENT].currentPageNo;
  const recipientOpenDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.OPEN].currentPageNo;
  const recipientClickDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.CLICK].currentPageNo;
  const recipientBounceDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.BOUNCE].currentPageNo;
  const recipientUnsubDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.UNSUB].currentPageNo;
  const recipientSpamDataPageNo =
    recipientData[RECIPIENT_EVENT_TYPE.SPAM].currentPageNo;

  useEffect(() => {
    if (timeRange.startDate && timeRange.endDate) {
      dispatch(getRecipientAllData());
      dispatch(getRecipientNotSentData());
      dispatch(getRecipientOpenData());
      dispatch(getRecipientClickData());
      dispatch(getRecipientBounceData());
      dispatch(getRecipientUnsubData());
      dispatch(getRecipientSpamData());
    }
  }, [dispatch, timeRange, templateId]);

  useEffect(() => {
    switch (recipientEventType) {
      case RECIPIENT_EVENT_TYPE.ALL:
        dispatch(getRecipientAllData());
        break;
      case RECIPIENT_EVENT_TYPE.NOT_SENT:
        dispatch(getRecipientNotSentData());
        break;
      case RECIPIENT_EVENT_TYPE.OPEN:
        dispatch(getRecipientOpenData());
        break;
      case RECIPIENT_EVENT_TYPE.CLICK:
        dispatch(getRecipientClickData());
        break;
      case RECIPIENT_EVENT_TYPE.BOUNCE:
        dispatch(getRecipientBounceData());
        break;
      case RECIPIENT_EVENT_TYPE.UNSUB:
        dispatch(getRecipientUnsubData());
        break;
      case RECIPIENT_EVENT_TYPE.SPAM:
        dispatch(getRecipientSpamData());
        break;
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    recipientAllDataPageNo,
    recipientNotsentDataPageNo,
    recipientOpenDataPageNo,
    recipientClickDataPageNo,
    recipientBounceDataPageNo,
    recipientUnsubDataPageNo,
    recipientSpamDataPageNo,
  ]);

  function switchTabData(type: RECIPIENT_EVENT_TYPE) {
    dispatch(setRecipientEventType(type));
  }

  return (
    <Tabs size="md" variant="enclosed-colored" mt={10} width="100%">
      <TabList borderBottom={1}>
        {Object.entries(RECIPIENT_EVENT_TYPE).map(([key, value], index) => (
          <Tab
            key={`${key}${index}`}
            onClick={() => switchTabData(value as RECIPIENT_EVENT_TYPE)}
          >
            <ITitle title={key.replaceAll("_", " ")} />
          </Tab>
        ))}
      </TabList>
      <Divider />
      <TabPanels>
        {Object.values(RECIPIENT_EVENT_TYPE).map((value, index) => {
          return (
            <TabPanel key={`${value}${index}`} px={0}>
              <RecipientTable
                type={value}
                showUserColumn={campaign_context === CAMPAIGN_CONTEXT.ORG}
              />
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
}
